import { set } from "date-fns";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface Props {
  children: React.ReactNode;
}

interface PortalContextValue {
  setPortalHost: (
    hostName: string,
    identifier: string,
    content: React.ReactNode,
  ) => void;
  clearPortalHost: (hostName: string, identifier: string) => void;
  getPortalHost: (hostName: string) => React.ReactNode | undefined;
}

type PortalHosts = Record<string, React.ReactNode | undefined>;

const PortalContext = createContext<PortalContextValue>(
  {} as PortalContextValue,
);

export function PortalProvider({ children }: Props) {
  const portalHostIdentifiers = useRef<Record<string, string>>({});
  const [portalHosts, setPortalHosts] = useState<PortalHosts>({});
  const portalIndex = useRef(1);

  const getPortalHost = useCallback(
    (hostName: string) => {
      return portalHosts[hostName];
    },
    [portalHosts],
  );

  const value: PortalContextValue = {
    setPortalHost: (
      hostName: string,
      identifier: string,
      content: React.ReactNode,
    ) => {
      portalHostIdentifiers.current[hostName] = identifier;
      setPortalHosts((prev) => ({
        ...prev,
        [hostName]: content,
      }));
    },
    clearPortalHost: (hostName: string, identifier: string) => {
      if (portalHostIdentifiers.current[hostName] !== identifier) {
        return;
      }
      setPortalHosts((prev) => ({
        ...prev,
        [hostName]: undefined,
      }));
    },
    getPortalHost,
  };

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
}

export function usePortalContext() {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error("Portal Context must be used within a PortalProvider");
  }
  return context;
}
